import { LiquidFlowRateUnits, GasFlowRateUnits } from "@/libs/units/flowRate";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { PressureUnits } from "@/libs/units/pressure";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";
import {
  getHpsSingleLiquidRateLabel,
  getHpsTotalLiquidRateLabel
} from "@/utils/hpsLiquidType";

export const buildWellTestSideCalculatorSection = ({
  scenarioCalculations,
  wellProperties
}: SectionDeps): Section => {
  const hpsLiquidType = wellProperties.value?.hps_liquid_type;

  return {
    id: EquipmentCalculatorSectionKey.WELL_TEST,
    hash: ScenarioDetailSectionKey.WELL_TEST,
    label: ScenarioDetailSectionTitle.WELL_TEST,
    routeName: "WellInfo",
    aggregateDisplays: [
      {
        label: getHpsTotalLiquidRateLabel(hpsLiquidType),
        base: LiquidFlowRateUnits.BarrelsPerDay,
        model: LiquidFlowRateUnits.options(),
        value: scenarioCalculations.wellTestFlowRates.value.liquidFlowRateSTBD,
        mode: "convert"
      },
      {
        label: "Productivity Index",
        base: ProductivityIndexUnits.BarrelDayPsi,
        model: ProductivityIndexUnits.options(),
        value: scenarioCalculations.productivityIndexStbdPsia.value,
        mode: "convert"
      },
      {
        label: "Oil Rate",
        base: LiquidFlowRateUnits.BarrelsPerDay,
        model: LiquidFlowRateUnits.options(),
        value: wellProperties.value?.well_test_oil_rate_stb_day,
        mode: "convert"
      },
      {
        label: getHpsSingleLiquidRateLabel(hpsLiquidType),
        base: LiquidFlowRateUnits.BarrelsPerDay,
        model: LiquidFlowRateUnits.options(),
        value: wellProperties.value?.well_test_water_rate_stb_day,
        mode: "convert"
      },
      {
        label: "Gas Rate",
        base: GasFlowRateUnits.ThousandCubicFeetPerDay,
        model: [GasFlowRateUnits.ThousandCubicFeetPerDay],
        value: wellProperties.value?.well_test_gas_rate_mscf_day,
        mode: "read"
      },
      {
        label: "GOR",
        base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
        model: LiquidRatioUnits.options(),
        value: scenarioCalculations.wellTestFlowRates.value.gasOilRatioSCFSTB,
        mode: "read"
      },
      {
        label: "GLR",
        base: LiquidRatioUnits.StandardCubicFootPerStockTankBarrel,
        model: LiquidRatioUnits.options(),
        value:
          scenarioCalculations.wellTestFlowRates.value.gasLiquidRatioSCFSTB,
        mode: "read"
      },
      {
        label: "Datum Point",
        base: LengthUnits.Feet,
        model: LargeLengthUnits.options(),
        value: wellProperties.value?.well_test_datum_point_measured_depth_feet,
        mode: "convert"
      },
      {
        label: "SBHP",
        base: PressureUnits.PoundsPerSquareInchAbsolute,
        model: PressureUnits.options(),
        value: wellProperties.value?.well_test_static_bottom_hole_pressure_psia,
        mode: "convert"
      },
      {
        label: "PBHP",
        base: PressureUnits.PoundsPerSquareInchAbsolute,
        model: PressureUnits.options(),
        value:
          wellProperties.value?.well_test_producing_bottom_hole_pressure_psia,
        mode: "convert"
      }
    ]
  };
};
